/**
 * PATH: packages/email-hub/src/feature/components/email-context-menu/ComposeAndCopyActions.tsx
 * FILE_ID: EMAILHUB-CONTEXTMENU-COMPOSE-COPY-ACTIONS-001
 * PURPOSE: Reply, forwarding, and clipboard actions for the email menu.
 * WHY: Keeps high-frequency communication actions in one domain group.
 */
import { Copy, Forward, Link2, Reply, ReplyAll } from 'lucide-react'
import { resolveCopiedAddress } from '@quotech/ui'

import {
  ContextMenuItem,
  ContextMenuSeparator,
} from '../../../_internal/components/ContextMenu'
import { verificationCodeSignalFor } from '../../action-signals'
import { emailPath } from '../../lib/basePath'
import type { EmailContextMenuController } from './useEmailContextMenuController'
import { VerificationCodeMenuActions } from './VerificationCodeMenuActions'

export function ComposeAndCopyActions({
  controller,
}: {
  controller: EmailContextMenuController
}) {
  const { ctxMenu, openCompose, setCtxMenu, toast } = controller
  if (!ctxMenu) return null

  const close = () => setCtxMenu(null)
  const email = ctxMenu.email
  const verificationSignal = verificationCodeSignalFor(email)

  return (
    <>
      <ContextMenuItem
        icon={Reply}
        onClick={() => {
          const emailId = email.id
          close()
          openCompose('reply', emailId)
        }}
      >
        Reply
      </ContextMenuItem>
      <ContextMenuItem
        icon={ReplyAll}
        onClick={() => {
          const emailId = email.id
          close()
          openCompose('reply_all', emailId)
        }}
      >
        Reply All
      </ContextMenuItem>
      <ContextMenuItem
        icon={Forward}
        onClick={() => {
          const emailId = email.id
          close()
          openCompose('forward', emailId)
        }}
      >
        Forward
      </ContextMenuItem>
      <ContextMenuSeparator />
      <ContextMenuItem
        icon={Copy}
        onClick={async () => {
          const address = resolveCopiedAddress(ctxMenu.pointerAddress, email.from_address)
          close()
          try {
            await navigator.clipboard.writeText(address)
            toast(`Copied ${address}`, 'success')
          } catch {
            toast('Couldn\u2019t copy - your browser blocked clipboard access.', 'error')
          }
        }}
      >
        Copy email address
      </ContextMenuItem>
      <ContextMenuItem
        icon={Link2}
        onClick={async () => {
          const link = `${window.location.origin}${emailPath(`/${email.id}`)}`
          close()
          try {
            await navigator.clipboard.writeText(link)
            toast('Link copied - opens this email for anyone on your org.', 'success')
          } catch {
            toast('Couldn\u2019t copy - your browser blocked clipboard access.', 'error')
          }
        }}
      >
        Copy link to email
      </ContextMenuItem>
      <VerificationCodeMenuActions
        email={email}
        signal={verificationSignal}
        onClose={close}
        toast={toast}
      />
      <ContextMenuSeparator />
    </>
  )
}
